// The fetch + Authorization: Bearer request helper — the single seam every
// authed API method goes through.
//
// Behaviour is byte-for-byte the historical `waytide-web` `request()`: JSON
// content-type by default, the Bearer header attached from the injected store
// on authed calls, a 401-on-an-authed-call clears the session (so a dead
// token stops firing), 204 → undefined, and any non-ok response throws an
// `ApiError` carrying the status + server message. The only change is that
// the base URL, the fetch impl, and the token source are injected (config +
// store) rather than read from module globals — which is exactly what makes
// it platform-neutral.

import type { ClientConfig } from "./config";
import { callFetch } from "./config";
import { ApiError } from "./errors";

/** A `fetch` init plus the `auth` flag the helper understands. `auth`
 * defaults to true; set it false on the pre-session endpoints (`/auth/start`,
 * `/auth/verify`, `/auth/share`) so a legitimate 401 there does not nuke an
 * unrelated existing session. */
export type RequestInitWithAuth = RequestInit & { auth?: boolean };

/** The bound request function a configured client carries. */
export type RequestFn = <T>(
  path: string,
  init?: RequestInitWithAuth,
) => Promise<T>;

/**
 * Build the `request<T>()` helper bound to a config (base URL + fetch + the
 * session-clear hook) and its store. The returned function is what every API
 * method calls.
 */
export function createRequest(config: ClientConfig): RequestFn {
  const { store, apiBase } = config;

  return async function request<T>(
    path: string,
    init: RequestInitWithAuth = {},
  ): Promise<T> {
    const { auth = true, headers, ...rest } = init;
    const finalHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...((headers as Record<string, string>) ?? {}),
    };
    if (auth) {
      const token = store.getToken();
      if (token) {
        finalHeaders["Authorization"] = `Bearer ${token}`;
      }
    }
    const resp = await callFetch(config, `${apiBase}${path}`, {
      ...rest,
      headers: finalHeaders,
    });
    if (!resp.ok) {
      // Read the body ONCE as text, then try to parse it as JSON. Calling
      // resp.json() first and resp.text() in the catch DOUBLE-READS the stream
      // — the failed json() has already locked it, so text() throws "body
      // stream already read", masking the real error (Brief 5 finding: this
      // surfaced as an opaque error on the repo-bind step when a call returned
      // a non-JSON error body).
      const raw = await resp.text().catch(() => "");
      let body: unknown = raw || null;
      try {
        if (raw) body = JSON.parse(raw);
      } catch {
        body = raw;
      }
      // When an authed call comes back 401 the local session is stale (server
      // identity wiped, token expired, secret rotated, etc.). Clearing here
      // means subsequent calls don't keep firing with the dead token. Skipped
      // on `auth: false` calls (like /auth/start) so a legitimate 401 there
      // doesn't nuke a real session.
      if (resp.status === 401 && auth) {
        store.clear();
        config.onSessionCleared?.();
      }
      // `statusText` is EMPTY over HTTP/2 (the protocol dropped reason
      // phrases), so an errorless-body failure must still name itself —
      // an ApiError with message "" reads as falsy and a UI that renders
      // `error ? … : spinner` spins forever (Brief 5 finding: the repo step
      // hung on a 404 exactly this way).
      const msg =
        (body as { message?: string } | null)?.message ||
        resp.statusText ||
        `HTTP ${resp.status}`;
      throw new ApiError(resp.status, msg, body);
    }
    if (resp.status === 204) return undefined as T;
    return (await resp.json()) as T;
  };
}
